import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import MuiLink from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";

import SEO from "@/components/SEO";
import {
  RESET_BROWSE_PROFILE_DATA,
  fetchUserProfile,
} from "@/store/browse/browseActions";
import AvatarUpload from "@/components/SignupProcess/AvatarUpload/AvatarUpload";
import LoadingComponent from "@/components/Loading/loadingComponent";
import NoMatch from "@/Pages/NoMatch";

import ProfileProgressBar from "./components/ProfileProgressBar";
import Visibility from "./components/Visibility";
import ProfileHeader from "./components/ProfileHeader";
import UserProjects from "./components/UserProjects";
import SocialLinks from "./components/SocialLinks";
import ProfileSummary from "./components/ProfileSummary";
import ProfileExperience from "./components/ProfileExperience";
import JudgeBio from "./components/JudgeBio";
import {
  contentPanelSx,
  contentSx,
  noPermissionSx,
  noPermissionWrapSx,
  pageSx,
  pageWrapSx,
  railDividerSx,
  sideBarSx,
} from "./styles";

const Profile = () => {
  const { t } = useTranslation(["translation.Profile"]);
  const dispatch = useDispatch();
  const { u_id: userIdParam } = useParams();

  const user = useSelector((state: any) => state.user);
  const browseProfile = useSelector((state: any) => state.browse?.browseProfile);
  const isSignup = useSelector((state: any) => Boolean(state.metadata.isSignup));
  const userLoaded = useSelector((state: any) =>
    Boolean(state.metadata.loading.get("userLoaded"))
  );
  const profileDataLoaded = useSelector((state: any) =>
    Boolean(state.metadata.loading.get("profileDataLoaded"))
  );

  const isUsersProfile = user.get("id") === parseInt(userIdParam ?? "", 10);

  const firstName = user?.toJS().first_name;
  const fullName = firstName ? `${firstName} ${user.toJS().last_name}` : null;

  const name = browseProfile?.getIn(["user", "first_name"])
    ? `${browseProfile.getIn(["user", "first_name"])} ${browseProfile.getIn([
        "user",
        "last_name",
      ])}`
    : null;

  // a user we cannot name and have finished loading does not exist; while the
  // request is still in flight we assume they do
  const doesUserExist =
    isUsersProfile || Boolean(name && browseProfile.get("hasLoaded"));

  const isLoading = !userLoaded && !profileDataLoaded;
  const isRequestingData = isUsersProfile
    ? isLoading
    : !browseProfile.get("hasLoaded");
  const isProfilePublic =
    isUsersProfile || browseProfile.get("isPublic") || user.get("jwt");

  const [isAvatarUploadModalOpen, setIsAvatarUploadModalOpen] =
    useState(isSignup);

  /**
   * Somebody else's profile is fetched once per user id. The class this
   * replaced kept a `dataRequested` flag and cleared it from inside
   * shouldComponentUpdate — a setState during render. The ref does the same
   * de-duplication without that.
   */
  const requestedFor = useRef<string | null>(null);
  useEffect(() => {
    if (isLoading || isUsersProfile || !userIdParam) return;
    if (requestedFor.current === userIdParam) return;
    requestedFor.current = userIdParam;
    dispatch(fetchUserProfile(userIdParam));
  }, [userIdParam, isLoading, isUsersProfile, dispatch]);

  // drop the browsed profile when we navigate to a different one, or away
  useEffect(
    () => () => {
      dispatch({ type: RESET_BROWSE_PROFILE_DATA });
    },
    [userIdParam, dispatch]
  );

  const openAvatarUpload = useCallback(
    () => setIsAvatarUploadModalOpen(true),
    []
  );
  const closeAvatarUpload = useCallback(
    () => setIsAvatarUploadModalOpen(false),
    []
  );

  if (isRequestingData) {
    return <LoadingComponent />;
  }

  if (!doesUserExist) {
    return <NoMatch />;
  }

  const renderNoPermission = () => (
    <Box sx={noPermissionSx}>
      <Box sx={noPermissionWrapSx}>
        <Stack spacing={2} sx={{ ...contentPanelSx, alignItems: "flex-start" }}>
          <Typography variant="h1">
            {t("View")} {firstName}&#39;{t("fullProfile")}
          </Typography>
          <Typography variant="h3">{t("ProfilePhrase1")}</Typography>
          <Button
            component={Link}
            to="/auth/sign-up"
            variant="contained"
            disableElevation
          >
            {t("signUpFree")}
          </Button>
          <MuiLink
            component={Link}
            to="/auth/login"
            underline="hover"
            sx={{ color: "common.white", fontStyle: "italic" }}
          >
            {t("or")} {t("Login")}
          </MuiLink>
        </Stack>
      </Box>
    </Box>
  );

  return (
    <SEO title={isUsersProfile ? fullName : name}>
      <Box sx={pageSx}>
        {isUsersProfile && <ProfileProgressBar />}
        <Box sx={pageWrapSx}>
          <Box sx={sideBarSx}>
            <ProfileHeader
              isUsersProfile={isUsersProfile}
              open={openAvatarUpload}
            />
            {isUsersProfile && (
              <>
                <Divider sx={railDividerSx} />
                <Visibility />
              </>
            )}
            <Divider sx={railDividerSx} />
            <SocialLinks isUsersProfile={isUsersProfile} />
            <Divider sx={railDividerSx} />
            <UserProjects isUsersProfile={isUsersProfile} />
          </Box>
          <Box sx={contentSx}>
            {!isProfilePublic ? (
              renderNoPermission()
            ) : (
              <Box>
                <ProfileSummary isUsersProfile={isUsersProfile} />
                <JudgeBio isUsersProfile={isUsersProfile} />
                <ProfileExperience isUsersProfile={isUsersProfile} />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <AvatarUpload
        isOpen={isAvatarUploadModalOpen}
        closeModal={closeAvatarUpload}
      />
    </SEO>
  );
};

export default Profile;
